<script setup lang="ts">
import VPModal from './VPModal.vue';
import VPButton from './VPButton.vue';
import VPFanVerificationIllustration from './VPFanVerificationIllustration.vue';
import VueTurnstile from 'vue-turnstile';
import { TurnstileCancelledError, useTurnstileStore } from '../turnstile';
import { onMounted, ref, watch } from 'vue';
import { RuntimeConfig } from '@vp/common/api';
import { useGettext } from 'vue3-gettext';

const { $gettext } = useGettext();

const turnstileStore = useTurnstileStore();
const token = ref<string>('');
const turnstile = ref<RuntimeConfig.RuntimeConfig['turnstile'] | null>(null);

onMounted(async () => {
  if (import.meta.env.SSR) {
    return;
  }

  const cfg = await RuntimeConfig.getRuntimeConfig();
  turnstile.value = cfg.turnstile;
});

watch(token, async (newToken: string) => {
  try {
    await turnstileStore.verifyToken(newToken);
    turnstileStore.resolved();
  } catch (e) {
    turnstileStore.failed(e);
  }
});

// A typed error rather than a message: the challenge is raised deep inside the
// Apollo link, so the call site that triggered it can only tell a dismissal apart
// from a real verification failure by the rejection reason.
const onCancel = () => {
  turnstileStore.failed(new TurnstileCancelledError());
};

// The trailing `!` on rounded-md/bg-neutral-0/p-6/gap-5 below is required rather
// than defensive. VPModal concatenates `props.class` onto a div that already
// hardcodes `rounded-sm bg-surface p-4 gap-2`, and Tailwind v4 emits utilities
// alphabetically, so `.rounded-sm` and `.bg-surface` outrank ours and would win
// silently -- the classes stay in the markup while the card renders at 4px radius
// on an off-white background. Giving VPModal real class merging (tailwind-merge)
// would let these drop, but that changes behaviour for every other consumer.
</script>

<template>
  <VPModal
    :show="turnstileStore?.open"
    :backdrop-clickable="false"
    class="w-[507px] max-w-full rounded-md! border border-neutral-300 bg-neutral-0! p-6! gap-5! shadow-[6px_6px_25px_#00000033]"
    @close="onCancel"
  >
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-4">
        <VPFanVerificationIllustration class="size-14 shrink-0" />
        <h2 class="text-2xl font-bold text-neutral-900">
          {{ $gettext("Fan Verification") }}
        </h2>
      </div>
      <VPButton
        variant="ghost-neutral"
        size="sm"
        icon-prefix="mdi:close"
        :aria-label="$gettext('Cancel')"
        @click="onCancel"
      />
    </div>

    <div class="flex flex-col gap-2 text-base text-neutral-700">
      <p>
        {{ $gettext("We're verifying that you are a real fan in order to provide the best experience for our customers, and deter bot traffic.") }}
      </p>
      <p>
        {{ $gettext('Once the verification is completed you will be able to cart tickets and check out.') }}
      </p>
    </div>

    <!--
      size="flexible" is what actually makes the widget span the modal: Cloudflare
      renders the iframe at the size named here, and the default ("normal") is a
      fixed ~300px. A `w-full` class would be inert -- VueTurnstile's root is a
      plain div that already stretches as a flex child, and the widget inside it
      is not ours to size.
    -->
    <VueTurnstile
      v-if="turnstile?.managedKiteKey"
      v-model="token"
      size="flexible"
      :site-key="turnstile.managedKiteKey"
    />

    <template #commands>
      <div class="flex">
        <VPButton
          variant="outline"
          @click="onCancel"
        >
          {{ $gettext('Cancel') }}
        </VPButton>
      </div>
    </template>
  </VPModal>
</template>
